// hooks/auth/useAuth2FASelect.ts
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectAuthError, selectAuthStatus, selectRegToken } from '@/store/selectors/auth';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import type { TwoFactorMethod } from '@/types/AuthState';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { isAutoPhoneVerificationOrganizationToken } from '@/store/utils/jwtDecoder';

interface UseAuth2FASelectState {
  isLoading: boolean;
  error: string | undefined;
  selectedMethod: TwoFactorMethod;
  isSmsMethodHidden: boolean;
}

interface UseAuth2FASelectHandlers {
  handleMethodChange: (val: string) => void;
  handleSendCode: () => void;
  handleBack: () => void;
}

interface UseAuth2FASelectReturn {
  state: UseAuth2FASelectState;
  handlers: UseAuth2FASelectHandlers;
}

/**
 * Hook for /login/2fa/select route
 * Handles 2FA method selection, code sending, and navigation
 */
export function useAuth2FASelect(): UseAuth2FASelectReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handlers: loaderHandlers } = useGlobalLoader();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const regToken = useAppSelector(selectRegToken);
  const isSmsMethodHidden = isAutoPhoneVerificationOrganizationToken(regToken);
  const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);
  const isLoading = authStatus === 'loading' || isLocallySubmitting;

  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>(
    isSmsMethodHidden ? 'email' : 'sms'
  );

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const handleMethodChange = useCallback((val: string): void => {
    if (val === 'sms' || val === 'email') {
      setSelectedMethod(val);
    }
  }, []);

  const handleSendCode = useCallback((): void => {
    if (isLoading) return;
    void (async () => {
      setIsLocallySubmitting(true);
      loaderHandlers.showLoader();
      try {
        const action = await dispatch(select2FAMethodThunk({ method: selectedMethod }));
        if (select2FAMethodThunk.fulfilled.match(action)) {
          await navigate({ to: '/login/2fa/verify' });
        }
      } finally {
        loaderHandlers.hideLoader();
        setIsLocallySubmitting(false);
      }
    })();
  }, [dispatch, navigate, isLoading, loaderHandlers, selectedMethod]);

  const handleBack = useCallback((): void => {
    void navigate({ to: '/login/2fa/select' });
  }, [navigate]);

  return {
    state: { isLoading, error, selectedMethod, isSmsMethodHidden },
    handlers: { handleMethodChange, handleSendCode, handleBack },
  };
}
